import { Dialog } from "@mui/material";
import { stopPropagation } from "@redotech/react-util/event";
import { memo } from "react";
import { Flex } from "../flex";
import * as css from "./merchant-customizable-modal.module.css";
import { useInertPageBehindModal } from "./use-inert-page-behind-modal";

export type ModalPlacement =
  | "center"
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export const MerchantCustomizableModal = memo(
  function MerchantCustomizableModal({
    open,
    onClose,
    children,
    fullscreen,
    backdropColor,
    ariaLabel,
    placement = "center",
    containerStyle,
    allowBackgroundInteraction = false,
    hideOverflow = false,
  }: {
    open: boolean;
    onClose: () => void;
    children?: React.ReactNode;
    fullscreen: boolean;
    backdropColor?: string;
    ariaLabel?: string;
    placement?: ModalPlacement;
    containerStyle?: React.CSSProperties;
    allowBackgroundInteraction?: boolean;
    hideOverflow?: boolean;
  }) {
    const positionStyle = getPlacementStyle(placement);
    const paperRef = useInertPageBehindModal(
      open && !allowBackgroundInteraction,
    );

    return (
      <>
        <style>{`.MuiBackdrop-root{background-color: ${backdropColor};}`}</style>
        <Dialog
          className={`${css.modalWrapper} ${fullscreen ? css.fullscreen : ""}`}
          disableEnforceFocus
          disableScrollLock={allowBackgroundInteraction}
          fullScreen={fullscreen}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") {
              onClose();
            }
          }}
          open={open}
          PaperProps={{
            ref: paperRef,
            tabIndex: -1,
            "aria-label": ariaLabel,
            "aria-modal": !allowBackgroundInteraction,
            style: {
              ...containerStyle,
              ...positionStyle,
              ...(allowBackgroundInteraction ? { pointerEvents: "auto" } : {}),
            },
          }}
          slotProps={
            allowBackgroundInteraction
              ? {
                  root: { style: { pointerEvents: "none" } },
                  backdrop: { style: { pointerEvents: "none" } },
                }
              : undefined
          }
        >
          <Flex
            className={fullscreen ? css.fullscreen : ""}
            flex={1}
            onClick={stopPropagation}
            overflow={hideOverflow ? "hidden" : undefined}
          >
            {children}
          </Flex>
        </Dialog>
      </>
    );
  },
);

function getPlacementStyle(placement: ModalPlacement): React.CSSProperties {
  switch (placement) {
    case "center":
      return { margin: "auto" };
    case "top-left":
      return { position: "fixed", top: 20, left: 20, margin: 0 };
    case "top-center":
      return {
        position: "fixed",
        top: 20,
        left: "50%",
        transform: "translateX(-50%)",
        margin: 0,
      };
    case "top-right":
      return { position: "fixed", top: 20, right: 20, margin: 0 };
    case "bottom-left":
      return { position: "fixed", bottom: 20, left: 20, margin: 0 };
    case "bottom-center":
      return {
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        margin: 0,
      };
    case "bottom-right":
      return { position: "fixed", bottom: 20, right: 20, margin: 0 };
  }
}
